import { formatInTimeZone } from 'date-fns-tz';
import { nanoid } from 'nanoid';
import pluralize from 'pluralize';
import {
  AutomationInput,
  AutomationWhereType,
  ContactInput,
  ContactType,
  CreateJobInput,
  CreateJobScope,
  JobStatus,
  JobTemplateInput,
  RepeatInput,
  SiteInput,
  Status,
} from '~/gql/graphql';
import { setClearDateFiltersOnNextJobsVisit } from '~/helpers/jobCreationHints';
import { base64EncodeUtf8 } from '~/helpers/string';
import { getTenantTimezone } from '~/helpers/timezoneUtils';
import HousePlaceholderImg from '~/images/images/HousePlaceholder.webp';

// =============================================================================
// TYPES
// =============================================================================

export interface OnboardingSiteData {
  id: string;
  name: string;
  type: string;
  addressData: string | null;
  status: Status;
  image?: string;
}

export interface OnboardingResult {
  siteId: string | null;
  jobId: string | null;
  profileJobId: string | null;
  site: OnboardingSiteData | null;
}

interface TenantContext {
  name?: string;
  contact_id?: string | null;
  time_zone?: string;
}

interface UserContext {
  name?: string;
  email?: string;
  phone?: string;
}

// =============================================================================
// PLACEHOLDER IMAGE UPLOAD
// =============================================================================

let cachedPlaceholderImageId: string | null = null;

export async function uploadPlaceholderImage(): Promise<string | null> {
  if (cachedPlaceholderImageId) return cachedPlaceholderImageId;

  try {
    const resp = await fetch(HousePlaceholderImg);
    if (!resp.ok) return null;

    const blob = await resp.blob();
    const file = new File([blob], 'HousePlaceholder.webp', {
      type: blob.type || 'image/webp',
    });

    const form = new FormData();
    form.append('files[]', file, file.name);

    const uploadResp = await fetch('/api/uploadv2', {
      method: 'POST',
      body: form,
    });

    const body = await uploadResp.json().catch(() => null);
    let id: string | null = null;

    if (body) {
      if (typeof body.id === 'string') id = body.id;
      else if (body.result && typeof body.result.id === 'string')
        id = body.result.id;
      else if (Array.isArray(body) && typeof body[0]?.id === 'string')
        id = body[0].id;
    }

    if (id) cachedPlaceholderImageId = id;
    return id ?? null;
  } catch (err) {
    console.error('Failed to upload placeholder image', err);
    return null;
  }
}

// =============================================================================
// ERROR HANDLING
// =============================================================================

export function extractGraphQLErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  const anyError = error as {
    message?: unknown;
    graphQLErrors?: Array<{ message?: unknown }>;
    networkError?: { message?: unknown };
  };

  const graphQLErrorMessage =
    anyError.graphQLErrors && anyError.graphQLErrors.length > 0
      ? anyError.graphQLErrors[0]?.message
      : undefined;

  if (typeof graphQLErrorMessage === 'string') {
    return graphQLErrorMessage;
  }

  if (
    anyError.networkError &&
    typeof anyError.networkError.message === 'string'
  ) {
    return anyError.networkError.message;
  }

  if (typeof anyError.message === 'string') {
    return anyError.message;
  }

  return null;
}

// =============================================================================
// SITE INPUT BUILDER
// =============================================================================

export function buildSiteInput(
  placeName: string,
  address: unknown,
  uploadedImageId: string | null,
  placeType: string | null = null
): SiteInput {
  return {
    status: Status.Active,
    name: placeName,
    type: placeType || 'Property', // Use selected place type, fallback to 'Property'
    ...(uploadedImageId ? { images: [uploadedImageId] } : {}),
    ...(address
      ? {
          address:
            typeof address === 'string' ? address : JSON.stringify(address),
        }
      : {}),
  };
}

// =============================================================================
// JOB INPUT BUILDERS
// =============================================================================

/**
 * Build a Draft.js formatted description containing checklist items.
 * Each item is rendered as a 'checklist-item' block (unchecked by default).
 * Used for frontend-defined checklists on job suggestions (e.g. Food & Beverage deep clean).
 *
 * @param items - Array of checklist item strings
 * @returns JSON string of Draft.js raw content state
 */
export function buildDraftChecklistDescription(items: string[]): string {
  const blocks = items.map((item, index) => ({
    key: `chk${index}`,
    text: item,
    type: 'checklist-item',
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: { checked: false },
  }));

  const raw = {
    blocks,
    entityMap: {},
  };

  return JSON.stringify(raw);
}

function buildDraftLinkDescription(label: string, url: string): string {
  const text = label;
  const entityKey = 0;
  const linkText = 'Open User Settings';
  const start = text.indexOf(linkText);
  const length = start >= 0 ? linkText.length : text.length;

  const raw = {
    blocks: [
      {
        key: 'init1',
        text,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges:
          start >= 0
            ? [
                {
                  offset: start,
                  length,
                  key: entityKey,
                },
              ]
            : [],
        data: {},
      },
    ],
    entityMap:
      start >= 0
        ? {
            [entityKey]: {
              type: 'LINK',
              mutability: 'MUTABLE',
              data: { url },
            },
          }
        : {},
  } as const;

  return JSON.stringify(raw);
}

export function buildFirstAssetJobInput(
  siteId: string,
  tenantTimezone: string,
  todayLocal: string,
  userContactId: string | null
): CreateJobInput {
  return {
    name: 'Add my first asset',
    scope: CreateJobScope.Site,
    scopeIds: [siteId],
    timeZone: tenantTimezone,
    scheduledStartStrict: '1',
    scheduledEndStrict: '0',
    scheduledStartDate: todayLocal,
    scheduledStartTime: null,
    scheduledEndDate: null,
    scheduledEndTime: null,
    tasks: [],
    notes:
      'Welcome to your first Job! Click start when you are ready and we will guide you through adding your first asset.',
    status: JobStatus.Accepted,
    assignee: userContactId ? [userContactId] : null,
    owner: userContactId ? [userContactId] : null,
    tags: [],
    fromTemplate: '',
    notify: false,
    includePdf: false,
    sendMeACopy: false,
    comment: '',
    attachments: [],
    customer: null,
    repeat: null,
    includeAttributes: null,
    jobAttributes: null,
    enforceOrder: false,
  };
}

export function buildProfileJobInput(
  siteId: string,
  tenantTimezone: string,
  todayLocal: string,
  userContactId: string | null
): CreateJobInput {
  const profilePicLink = '#user-settings-open:profilePicture';
  const nameLink = '#user-settings-open:personal';

  return {
    name: 'Complete my profile',
    scope: CreateJobScope.Site,
    scopeIds: [siteId],
    timeZone: tenantTimezone,
    scheduledStartStrict: '1',
    scheduledEndStrict: '0',
    scheduledStartDate: todayLocal,
    scheduledStartTime: null,
    scheduledEndDate: null,
    scheduledEndTime: null,
    tasks: [
      {
        id: nanoid(),
        type: 'Standard',
        name: 'Add a profile picture',
        description: buildDraftLinkDescription(
          'Open User Settings to add a profile picture',
          profilePicLink
        ),
      },
      {
        id: nanoid(),
        type: 'Standard',
        name: 'Add my name',
        description: buildDraftLinkDescription(
          'Open User Settings to add your name',
          nameLink
        ),
      },
    ],
    notes:
      'A quick profile setup to personalise your experience. This will also help you understand how jobs with tasks work.',
    status: JobStatus.Accepted,
    assignee: userContactId ? [userContactId] : null,
    owner: userContactId ? [userContactId] : null,
    tags: [],
    fromTemplate: '',
    notify: false,
    includePdf: false,
    sendMeACopy: false,
    comment: '',
    attachments: [],
    customer: null,
    repeat: null,
    includeAttributes: null,
    jobAttributes: null,
    enforceOrder: false,
  };
}

// =============================================================================
// RECURRING ASSET JOB INPUT BUILDER
// =============================================================================

/**
 * Frequency types supported by onboarding.
 * - 'fortnightly' maps to 'weekly' with interval 2
 * - 'yearly' maps to 'monthly' with interval 12 (backend doesn't support yearly)
 * - 'custom' uses the customUnit for actual frequency
 */
export type OnboardingFrequencyType =
  | 'daily'
  | 'weekly'
  | 'fortnightly'
  | 'monthly'
  | 'yearly'
  | 'custom';

/**
 * Type for seeded template task data from GraphQL query.
 * Used to copy task content (including description/checklist) from starter templates.
 * This type is intentionally loose to accommodate GraphQL response variations.
 */
export interface SeededTemplateTask {
  id: string;
  type: string;
  name: string;
  description: string | null;
  attributes?: Array<{
    id: string;
    type: string;
    category?: string | null;
    name: string;
    value: unknown;
  }> | null;
  jobAttributeIds?: string[] | null;
  tags?: {
    nodes?: Array<{
      id: string;
      entityType: string;
      name: string;
      category?: string | null;
      description?: string | null;
      colour?: string | null;
    }>;
  };
  config?: {
    selection?: string;
    spaces?: string[];
    assetTypes?: string[];
    allow?: string[];
    shareAttributes?: boolean;
    shareJobHistory?: boolean;
    shareDeploymentHistory?: boolean;
  } | null;
  assets?: Array<{ id: string; name: string; type: string }> | null;
  attribute?: {
    id: string;
    attributes: string[];
  } | null;
}

export interface RecurringAssetJobConfig {
  assetIds: string[]; // Array of asset IDs - one task per asset will be created
  assetName: string; // Primary asset name (for backwards compatibility)
  assetNames?: string[]; // Multiple asset names when creating one job for multiple assets
  assetIdToNameMap?: Record<string, string>; // Map of asset ID to asset name (for correct task titles when multiple different assets)
  assetIdToTypeMap?: Record<string, string>; // Map of asset ID to asset type (for task assetTypes field)
  assetIdToSpaceMap?: Record<string, string>; // Map of asset ID to space name (for task spaces field)
  jobName: string;
  freq: OnboardingFrequencyType;
  interval: string;
  byday?: string[]; // Full day names like 'Monday', will be converted to 2-letter codes
  bymonthday?: string;
  startDate: string; // YYYY-MM-DD format
  count?: number; // undefined = indefinite
  customUnit?: 'daily' | 'weekly' | 'monthly' | 'yearly'; // For custom frequency
  seededTemplateTasks?: SeededTemplateTask[]; // Optional tasks from seeded template to copy content (description/checklist)
  fallbackTaskDescription?: string | null; // Optional Draft.js description to use when no seeded template matches (e.g. frontend-defined checklists)
}

/**
 * Converts full day names to 2-letter lowercase codes expected by the backend.
 * e.g., 'Monday' -> 'mo', 'Tuesday' -> 'tu'
 * Matches the format used by repeatToInput in JobsRepeat.tsx
 */
function convertDayToCode(day: string): string {
  return day.substring(0, 2).toLowerCase();
}

/**
 * Normalizes frequency configuration for backend consumption.
 * Handles fortnightly (weekly with interval 2), yearly (monthly with interval 12),
 * and custom (uses customUnit).
 *
 * NOTE: Backend automation API only supports 'daily', 'weekly', 'monthly'.
 * 'yearly' is converted to 'monthly' with interval 12.
 */
function normalizeFrequency(config: RecurringAssetJobConfig): {
  freq: 'daily' | 'weekly' | 'monthly';
  interval: string;
  byday?: string[];
} {
  const { freq, interval, byday, customUnit } = config;

  // Convert byday from full names to 2-letter codes
  const normalizedByday =
    byday && byday.length > 0 ? byday.map(convertDayToCode) : undefined;

  switch (freq) {
    case 'fortnightly':
      // Fortnightly is weekly with interval 2
      return {
        freq: 'weekly',
        interval: '2',
        byday: normalizedByday,
      };
    case 'custom':
      // Custom uses the underlying customUnit as the actual frequency
      // For 'yearly' customUnit, convert to monthly with 12x interval
      if (customUnit === 'yearly') {
        const yearlyInterval = parseInt(interval, 10) || 1;
        return {
          freq: 'monthly',
          interval: String(yearlyInterval * 12),
          byday: normalizedByday,
        };
      }
      return {
        freq: customUnit || 'weekly',
        interval,
        byday: normalizedByday,
      };
    case 'yearly': {
      // Backend doesn't support 'yearly', convert to monthly with interval * 12
      // e.g., "every 1 year" = "every 12 months"
      const yearInterval = parseInt(interval, 10) || 1;
      return {
        freq: 'monthly',
        interval: String(yearInterval * 12),
        byday: normalizedByday,
      };
    }
    default:
      return {
        freq,
        interval,
        byday: normalizedByday,
      };
  }
}

/**
 * Formats asset names for display, grouping by type and pluralizing when needed.
 * Preserves capitalization from the original names.
 * Examples:
 * - ["Refrigerator", "Refrigerator"] → "Refrigerators"
 * - ["Refrigerator", "Refrigerator", "Lawn Mower"] → "Refrigerators and Lawn Mower"
 * - ["Refrigerator", "Lawn Mower"] → "Refrigerator and Lawn Mower"
 */
export function formatAssetNamesForDisplay(assetNames: string[]): string {
  if (assetNames.length === 0) return '';
  if (assetNames.length === 1) return assetNames[0];

  // Group assets by name and count occurrences
  const nameCounts = new Map<string, number>();
  assetNames.forEach((name) => {
    const count = nameCounts.get(name) || 0;
    nameCounts.set(name, count + 1);
  });

  // Build display parts: pluralize when count > 1, keep singular when count === 1
  // pluralize() preserves capitalization, so "Refrigerator" → "Refrigerators"
  const parts: string[] = [];
  nameCounts.forEach((count, name) => {
    if (count > 1) {
      parts.push(pluralize(name));
    } else {
      parts.push(name);
    }
  });

  // Join with "and" for multiple types, or return single part
  if (parts.length === 1) {
    return parts[0];
  } else if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  } else {
    // For 3+ types: "refrigerators, lawn mowers, and pool pumps"
    return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }
}

/**
 * Shared configuration for task generation from seeded templates.
 * Contains the maps and values needed for task creation.
 */
interface TaskGenerationConfig {
  jobName: string;
  assetName: string;
  assetIdToNameMap?: Record<string, string>;
  assetIdToTypeMap?: Record<string, string>;
  assetIdToSpaceMap?: Record<string, string>;
}

/**
 * Context information for a single asset when generating tasks.
 * Contains pre-computed values for task creation.
 */
interface AssetTaskContext {
  assetId: string;
  index: number;
  assetNameForTask: string;
  assetType: string | undefined;
  assetSpace: string | undefined;
  hasMultipleDifferentAssets: boolean;
  taskTitle: string;
}

/**
 * Callback function to map a template task and asset context to the final task shape.
 * Used to handle differences between JobTask and TemplateTask types.
 * @param templateTask - The seeded template task (null for fallback tasks without template)
 * @param assetContext - Asset context for Asset tasks (null for non-Asset tasks)
 * @param assetCount - Total count of unique assets
 */
type TaskMapper<T> = (
  templateTask: SeededTemplateTask | null,
  assetContext: AssetTaskContext | null,
  assetCount: number
) => T[];

/**
 * Generates tasks from seeded template tasks, handling shared filtering, title construction,
 * and asset-type/space lookups. Uses a taskMapper callback to produce the final task shape,
 * allowing different output formats for JobTask vs TemplateTask.
 *
 * @param seededTemplateTasks - Optional array of tasks from seeded template
 * @param uniqueAssetIds - Deduplicated array of asset IDs
 * @param config - Configuration maps for job name, asset names, types, and spaces
 * @param assetCount - Total count of unique assets
 * @param taskMapper - Callback function to map template task + asset context to final task shape
 * @returns Array of tasks in the format determined by taskMapper
 */
function generateTasksFromSeededTemplate<T>(
  seededTemplateTasks: SeededTemplateTask[] | undefined,
  uniqueAssetIds: string[],
  config: TaskGenerationConfig,
  assetCount: number,
  taskMapper: TaskMapper<T>
): T[] {
  if (seededTemplateTasks && seededTemplateTasks.length > 0) {
    // First pass: collect which asset types are matched by specific tasks
    // This is used to determine which assets should get universal tasks
    const matchedAssetTypes = new Set<string>();
    seededTemplateTasks.forEach((task) => {
      if (task.type === 'Asset') {
        const taskAssetTypes = task.config?.assetTypes;
        // Only count specific tasks (with assetTypes restriction)
        if (taskAssetTypes && taskAssetTypes.length > 0) {
          // Get all asset types from the current batch of assets
          const currentBatchAssetTypes = new Set(
            uniqueAssetIds
              .map((assetId) => config.assetIdToTypeMap?.[assetId])
              .filter((type): type is string => Boolean(type))
          );
          // Mark asset types as matched if they appear in both the task and the batch
          taskAssetTypes.forEach((taskAssetType) => {
            if (currentBatchAssetTypes.has(taskAssetType)) {
              matchedAssetTypes.add(taskAssetType);
            }
          });
        }
      }
    });

    // Copy tasks from seeded template, adapting them for specific assets
    return seededTemplateTasks.flatMap((templateTask): T[] => {
      // For Asset tasks, create one task per asset ID (only if asset type matches)
      if (templateTask.type === 'Asset') {
        const taskAssetTypes = templateTask.config?.assetTypes;
        const isUniversalTask = !taskAssetTypes || taskAssetTypes.length === 0;

        // Filter assets based on task assetTypes restriction
        const matchingAssetIds = uniqueAssetIds.filter((assetId) => {
          // Universal tasks (no assetTypes restriction) only apply to assets
          // whose type has no specific-task match
          if (isUniversalTask) {
            const assetType = config.assetIdToTypeMap?.[assetId];
            // Only include this asset if its type is not matched by any specific task
            return assetType ? !matchedAssetTypes.has(assetType) : false;
          }

          // For specific tasks, only include assets whose type matches
          const assetType = config.assetIdToTypeMap?.[assetId];
          return assetType && taskAssetTypes.includes(assetType);
        });

        // Create one task per matching asset
        return matchingAssetIds
          .map((assetId, index) => {
            // Determine asset name for this task
            const assetNameForTask =
              config.assetIdToNameMap?.[assetId] ?? config.assetName;

            // Determine if we have multiple different assets (use map) vs multiple units (use index)
            const hasMultipleDifferentAssets = Boolean(
              config.assetIdToNameMap &&
                Object.keys(config.assetIdToNameMap).length > 1 &&
                new Set(Object.values(config.assetIdToNameMap)).size > 1
            );

            // Build task title based on asset count and type
            const taskTitle = hasMultipleDifferentAssets
              ? `${config.jobName} ${assetNameForTask}`
              : assetCount > 1
              ? `${config.jobName} ${assetNameForTask} #${index + 1}`
              : `${config.jobName} ${assetNameForTask}`;

            // Get asset type and space from maps if available
            const assetType = config.assetIdToTypeMap?.[assetId];
            const assetSpace = config.assetIdToSpaceMap?.[assetId];

            // Create asset context for taskMapper
            const assetContext: AssetTaskContext = {
              assetId,
              index,
              assetNameForTask,
              assetType,
              assetSpace,
              hasMultipleDifferentAssets,
              taskTitle,
            };

            // Use taskMapper to create the final task shape
            return taskMapper(templateTask, assetContext, assetCount);
          })
          .flat();
      } else {
        // For non-Asset tasks, pass null asset context (no asset-specific processing needed)
        return taskMapper(templateTask, null, assetCount);
      }
    });
  } else {
    // No seeded template - create simple tasks from scratch
    return uniqueAssetIds
      .map((assetId, index) => {
        // Use assetIdToNameMap if available (for multiple different assets)
        // Otherwise fall back to assetName (for multiple units of same asset or single asset)
        const assetNameForTask =
          config.assetIdToNameMap?.[assetId] ?? config.assetName;

        // Determine if we have multiple different assets (use map) vs multiple units (use index)
        const hasMultipleDifferentAssets = Boolean(
          config.assetIdToNameMap &&
            Object.keys(config.assetIdToNameMap).length > 1 &&
            new Set(Object.values(config.assetIdToNameMap)).size > 1
        );

        // Build task title based on asset count and type
        const taskTitle = hasMultipleDifferentAssets
          ? `${config.jobName} ${assetNameForTask}`
          : assetCount > 1
          ? `${config.jobName} ${assetNameForTask} #${index + 1}`
          : `${config.jobName} ${assetNameForTask}`;

        // Get asset type and space from maps if available
        const assetType = config.assetIdToTypeMap?.[assetId];
        const assetSpace = config.assetIdToSpaceMap?.[assetId];

        // Create asset context for taskMapper
        const assetContext: AssetTaskContext = {
          assetId,
          index,
          assetNameForTask,
          assetType,
          assetSpace,
          hasMultipleDifferentAssets,
          taskTitle,
        };

        // Use taskMapper to create the final task shape (pass null templateTask for fallback)
        return taskMapper(null, assetContext, assetCount);
      })
      .flat();
  }
}

/**
 * Builds a job input for a recurring asset maintenance job.
 * - Location = site
 * - Owner = current user
 * - Assignee = no one (null)
 * - Name = job name from config
 * - Tasks = One Asset task per asset ID, with title derived from job name + asset name
 *
 * Handles frequency normalization:
 * - 'fortnightly' → 'weekly' with interval 2
 * - 'custom' → uses customUnit as actual frequency
 * - byday values converted from full names ('Monday') to 2-letter codes ('mo')
 */
export function buildRecurringAssetJobInput(
  siteId: string,
  tenantTimezone: string,
  userContactId: string | null,
  config: RecurringAssetJobConfig
): CreateJobInput {
  // Deduplicate asset IDs to prevent creating duplicate tasks
  // This can happen if asset IDs are accidentally duplicated during collection
  const uniqueAssetIds = Array.from(new Set(config.assetIds));
  const assetCount = uniqueAssetIds.length;

  // Validate that assetNames length matches unique assetIds length if assetNames is provided
  let assetNameDisplay: string;
  if (
    config.assetNames &&
    config.assetNames.length > 0 &&
    config.assetNames.length !== uniqueAssetIds.length
  ) {
    // Mismatch detected - log warning and fall back to single asset name
    console.warn(
      `[buildRecurringAssetJobInput] Asset names array length (${config.assetNames.length}) does not match unique asset IDs length (${uniqueAssetIds.length}). Falling back to single asset name.`
    );
    assetNameDisplay = config.assetName;
  } else if (
    assetCount > 1 &&
    config.assetNames &&
    config.assetNames.length > 1
  ) {
    // Multiple different assets - group by type, pluralize when needed, join with "and"
    assetNameDisplay = formatAssetNamesForDisplay(config.assetNames);
  } else if (assetCount > 1) {
    // Multiple units of same asset - pluralize
    assetNameDisplay = pluralize(config.assetName);
  } else {
    // Single asset
    assetNameDisplay = config.assetName;
  }
  const jobName = `${config.jobName} ${assetNameDisplay}`;

  // Normalize frequency (handles fortnightly, custom, byday conversion)
  const { freq: normalizedFreq, interval, byday } = normalizeFrequency(config);

  // Build repeat configuration based on normalized frequency
  const repeat: RepeatInput = {
    freq: normalizedFreq,
    interval,
    ...(byday && byday.length > 0 ? { byday } : {}),
    ...(config.bymonthday ? { bymonthday: config.bymonthday } : {}),
    ...(config.count ? { count: config.count } : {}),
  };

  // Create tasks - either from seeded template or from scratch
  // For multiple different assets: "Clean Refrigerator", "Clean Smoke Alarm", etc.
  // For multiple units of same asset: "Clean Pool Pump #1", "Clean Pool Pump #2", etc.
  // For single asset: "Clean Pool Pump"
  const { seededTemplateTasks } = config;

  // If we have seeded template tasks, use them as a base and adapt for specific assets
  // This preserves the description/checklist content from the template
  type JobTask = {
    id: string;
    type: string;
    name: string;
    description: string | null;
    asset?: {
      selection: string;
      assets: string[];
      allow: string[];
    };
  };

  // Task mapper for JobTask format
  const jobTaskMapper: TaskMapper<JobTask> = (
    templateTask,
    assetContext,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _assetCount
  ) => {
    // Fallback case: no template task, create simple Asset task
    // Uses fallbackTaskDescription (e.g. frontend-defined checklist) when no backend template matched
    if (!templateTask && assetContext) {
      return [
        {
          id: nanoid(),
          type: 'Asset',
          name: assetContext.taskTitle,
          description: config.fallbackTaskDescription ?? null,
          asset: {
            selection: 'specific',
            assets: [assetContext.assetId],
            ...(assetContext.assetSpace
              ? { spaces: [assetContext.assetSpace] }
              : {}),
            ...(assetContext.assetType
              ? { assetTypes: [assetContext.assetType] }
              : {}),
            allow: ['edit'],
          },
        },
      ];
    }

    // Template task case
    if (!templateTask) return [];

    // Asset task case: use asset context to build task
    if (assetContext) {
      return [
        {
          id: nanoid(),
          type: templateTask.type,
          name: assetContext.taskTitle,
          description: templateTask.description, // Preserve description/checklist from template
          asset: {
            selection: 'specific',
            assets: [assetContext.assetId],
            ...(assetContext.assetSpace
              ? { spaces: [assetContext.assetSpace] }
              : {}),
            ...(assetContext.assetType
              ? { assetTypes: [assetContext.assetType] }
              : {}),
            allow:
              templateTask.config?.allow && templateTask.config.allow.length > 0
                ? templateTask.config.allow
                : ['edit'],
          },
        },
      ];
    }

    // Non-Asset task case: copy as-is with original content
    return [
      {
        id: nanoid(),
        type: templateTask.type,
        name: templateTask.name,
        description: templateTask.description, // Preserve description/checklist from template
      },
    ];
  };

  const tasks = generateTasksFromSeededTemplate(
    seededTemplateTasks,
    uniqueAssetIds,
    {
      jobName: config.jobName,
      assetName: config.assetName,
      assetIdToNameMap: config.assetIdToNameMap,
      assetIdToTypeMap: config.assetIdToTypeMap,
      assetIdToSpaceMap: config.assetIdToSpaceMap,
    },
    assetCount,
    jobTaskMapper
  );

  // Get display-friendly frequency for notes
  const freqDisplay =
    config.freq === 'fortnightly' ? 'fortnightly' : normalizedFreq;

  // Build notes with proper asset name display (same logic as job name)
  // Validate assetNames length matches unique assetIds length
  const notesAssetDisplay =
    assetCount > 1 &&
    config.assetNames &&
    config.assetNames.length > 1 &&
    config.assetNames.length === uniqueAssetIds.length
      ? formatAssetNamesForDisplay(config.assetNames) // Multiple different assets - group, pluralize, join with "and"
      : assetCount > 1
      ? pluralize(config.assetName) // Multiple units of same asset - pluralize
      : config.assetName; // Single asset

  return {
    name: jobName,
    scope: CreateJobScope.Site,
    scopeIds: [siteId],
    timeZone: tenantTimezone,
    scheduledStartStrict: '1',
    scheduledEndStrict: '0',
    scheduledStartDate: config.startDate,
    scheduledStartTime: null,
    scheduledEndDate: null,
    scheduledEndTime: null,
    tasks,
    notes: `Recurring ${freqDisplay} job for ${notesAssetDisplay}`,
    status: JobStatus.Accepted,
    assignee: null, // No assignee as per requirements
    owner: userContactId ? [userContactId] : null,
    tags: [],
    fromTemplate: '',
    notify: false,
    includePdf: false,
    sendMeACopy: false,
    comment: '',
    attachments: [],
    customer: null,
    repeat,
    includeAttributes: null,
    jobAttributes: null,
    enforceOrder: false,
  };
}

// =============================================================================
// CONTACT CREATION
// =============================================================================

export function buildContactInput(user: UserContext): ContactInput {
  return {
    status: Status.Active,
    type: ContactType.Person,
    name: user.name || user.email || 'User',
    email: user.email,
    phone: user.phone || undefined,
    relationship: ['team member'],
  };
}

// =============================================================================
// DATE UTILITIES
// =============================================================================

export function getTodayInTenantTimezone(tenant: TenantContext | null): {
  timezone: string;
  todayLocal: string;
} {
  const timezone = getTenantTimezone(tenant);
  const todayLocal = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  return { timezone, todayLocal };
}

// =============================================================================
// LOCAL STORAGE HELPERS
// =============================================================================

export function saveOnboardingJobIds(ids: string[]): void {
  try {
    if (ids.length > 0) {
      window.localStorage.setItem('onboardingJobs', JSON.stringify(ids));
    }
  } catch (err) {
    console.debug('localStorage unavailable for onboardingJobs', err);
  }
}

export function markOnboardingComplete(): void {
  try {
    window.localStorage.setItem('postSignupTourPending', '1');
  } catch (err) {
    console.debug('localStorage unavailable for postSignupTourPending', err);
  }

  try {
    window.localStorage.setItem(
      'onboardingJustFinished',
      JSON.stringify({ timestamp: Date.now() })
    );
  } catch (err) {
    console.debug('localStorage unavailable for onboardingJustFinished', err);
  }

  // Clear date filters on next jobs page visit so newly created jobs are visible
  // This is simpler than expanding filters to include specific job dates
  setClearDateFiltersOnNextJobsVisit();
}

// =============================================================================
// ONBOARDING RESUME (CONTINUE SETUP)
// =============================================================================

/**
 * Steps that can be resumed when continuing onboarding setup
 */
export type OnboardingResumeStep = 'spaces' | 'assets' | 'jobs';

export interface OnboardingResumeData {
  step: OnboardingResumeStep;
  siteId: string;
  placeName: string;
  industry: string;
}

const ONBOARDING_RESUME_KEY = 'onboardingResume';

/**
 * Save onboarding resume data to localStorage
 */
export function setOnboardingResume(data: OnboardingResumeData): void {
  try {
    window.localStorage.setItem(ONBOARDING_RESUME_KEY, JSON.stringify(data));
  } catch (err) {
    console.debug('localStorage unavailable for onboardingResume', err);
  }
}

/**
 * Get onboarding resume data from localStorage
 */
export function getOnboardingResume(): OnboardingResumeData | null {
  try {
    const data = window.localStorage.getItem(ONBOARDING_RESUME_KEY);
    if (!data) return null;
    return JSON.parse(data) as OnboardingResumeData;
  } catch (err) {
    console.debug('localStorage unavailable for onboardingResume', err);
    return null;
  }
}

/**
 * Clear onboarding resume data from localStorage
 */
export function clearOnboardingResume(): void {
  try {
    window.localStorage.removeItem(ONBOARDING_RESUME_KEY);
  } catch (err) {
    console.debug('localStorage unavailable for onboardingResume', err);
  }
}

/**
 * Build a Draft.js formatted notes field with a CTA link
 */
function buildNotesWithCTA(
  description: string,
  ctaText: string,
  ctaUrl: string
): string {
  const fullText = `${description}\n\n${ctaText}`;
  const ctaStart = description.length + 2; // +2 for the newlines
  const ctaLength = ctaText.length;

  const raw = {
    blocks: [
      {
        key: 'desc1',
        text: fullText,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [
          {
            offset: ctaStart,
            length: ctaLength,
            key: 0,
          },
        ],
        data: {},
      },
    ],
    entityMap: {
      '0': {
        type: 'LINK',
        mutability: 'MUTABLE',
        data: { url: ctaUrl },
      },
    },
  };

  return JSON.stringify(raw);
}

/**
 * Job configurations for each resume step
 */
const CONTINUE_JOB_CONFIGS: Record<
  OnboardingResumeStep,
  {
    getJobName: (placeName: string) => string;
    description: string;
    ctaText: string;
  }
> = {
  spaces: {
    getJobName: (placeName) => `Set up ${placeName}`,
    description:
      'Add spaces like rooms or zones to organize your property. This helps you track where your assets are located.',
    ctaText: '👉 Continue Setup',
  },
  assets: {
    getJobName: () => 'Add assets',
    description:
      'Add your appliances, equipment, or vehicles that need regular maintenance. Assets help you track what needs servicing.',
    ctaText: '👉 Continue Setup',
  },
  jobs: {
    getJobName: () => 'Create jobs for my assets',
    description:
      'Set up recurring maintenance jobs to keep your assets in top shape. Schedule regular servicing so nothing gets forgotten.',
    ctaText: '👉 Continue Setup',
  },
};

/**
 * Builds a job input for continuing onboarding setup.
 * Creates a job with a description and CTA link that reopens onboarding at the appropriate step.
 *
 * @param siteId - The site ID for the job
 * @param tenantTimezone - Timezone for scheduling
 * @param todayLocal - Today's date in local format (YYYY-MM-DD)
 * @param userContactId - Contact ID to assign the job to
 * @param resumeStep - Which step to resume ('spaces', 'assets', or 'jobs')
 * @param placeName - Name of the place/property
 */
export function buildContinueOnboardingJobInput(
  siteId: string,
  tenantTimezone: string,
  todayLocal: string,
  userContactId: string | null,
  resumeStep: OnboardingResumeStep,
  placeName: string,
  industry: string
): CreateJobInput {
  const config = CONTINUE_JOB_CONFIGS[resumeStep];
  const jobName = config.getJobName(placeName);

  // Build the CTA URL that will trigger the resume
  // Format: #onboarding-resume:step:siteId:placeName:industry (base64 encoded to handle special chars)
  const resumeData = base64EncodeUtf8(
    JSON.stringify({ step: resumeStep, siteId, placeName, industry })
  );
  const ctaUrl = `#onboarding-resume:${resumeData}`;

  const notes = buildNotesWithCTA(config.description, config.ctaText, ctaUrl);

  return {
    name: jobName,
    scope: CreateJobScope.Site,
    scopeIds: [siteId],
    timeZone: tenantTimezone,
    scheduledStartStrict: '1',
    scheduledEndStrict: '0',
    scheduledStartDate: todayLocal,
    scheduledStartTime: null,
    scheduledEndDate: null,
    scheduledEndTime: null,
    tasks: [],
    notes,
    status: JobStatus.Accepted,
    assignee: userContactId ? [userContactId] : null,
    owner: userContactId ? [userContactId] : null,
    tags: [],
    fromTemplate: '',
    notify: false,
    includePdf: false,
    sendMeACopy: false,
    comment: '',
    attachments: [],
    customer: null,
    repeat: null,
    includeAttributes: null,
    jobAttributes: null,
    enforceOrder: false,
  };
}

// =============================================================================
// AUTOMATION BUILDERS (FOR "FOREVER" RECURRING JOBS)
// =============================================================================

export interface RecurringAssetAutomationConfig {
  assetIds: string[]; // Array of asset IDs - one task per asset will be created
  assetName: string; // Primary asset name (for backwards compatibility)
  assetNames?: string[]; // Multiple asset names when creating one job for multiple assets
  assetIdToNameMap?: Record<string, string>; // Map of asset ID to asset name (for correct task titles when multiple different assets)
  assetIdToTypeMap?: Record<string, string>; // Map of asset ID to asset type (for filtering tasks by asset type)
  assetIdToSpaceMap?: Record<string, string>; // Map of asset ID to space name (for task spaces field)
  jobName: string;
  siteName?: string; // Property/site name for template naming
  freq: OnboardingFrequencyType;
  interval: string;
  byday?: string[]; // Full day names like 'Monday', will be converted to 2-letter codes
  bymonthday?: string;
  startDate: string; // YYYY-MM-DD format
  customUnit?: 'daily' | 'weekly' | 'monthly' | 'yearly'; // For custom frequency
  maintainRepeats?: number; // Number of jobs to maintain ahead (default: 10)
  fallbackTaskDescription?: string | null; // Optional Draft.js description to use when no seeded template matches (e.g. frontend-defined checklists)
}

/**
 * Builds a job template input for a recurring asset automation.
 * The template defines what the job looks like (name, tasks, etc.)
 * Creates one task per asset when multiple asset IDs are provided.
 *
 * Handles frequency normalization for display-friendly notes.
 */
export function buildRecurringAssetTemplateInput(
  config: RecurringAssetAutomationConfig,
  seededTemplateTasks?: SeededTemplateTask[]
): JobTemplateInput {
  // Deduplicate asset IDs to prevent creating duplicate tasks
  // This can happen if asset IDs are accidentally duplicated during collection
  const uniqueAssetIds = Array.from(new Set(config.assetIds));
  const assetCount = uniqueAssetIds.length;

  // Validate that assetNames length matches unique assetIds length if assetNames is provided
  let assetNameDisplay: string;
  if (
    config.assetNames &&
    config.assetNames.length > 0 &&
    config.assetNames.length !== uniqueAssetIds.length
  ) {
    // Mismatch detected - log warning and fall back to single asset name
    console.warn(
      `[buildRecurringAssetTemplateInput] Asset names array length (${config.assetNames.length}) does not match unique asset IDs length (${uniqueAssetIds.length}). Falling back to single asset name.`
    );
    assetNameDisplay = config.assetName;
  } else if (
    assetCount > 1 &&
    config.assetNames &&
    config.assetNames.length > 1
  ) {
    // Multiple different assets - group by type, pluralize when needed, join with "and"
    assetNameDisplay = formatAssetNamesForDisplay(config.assetNames);
  } else if (assetCount > 1) {
    // Multiple units of same asset - pluralize
    assetNameDisplay = pluralize(config.assetName);
  } else {
    // Single asset
    assetNameDisplay = config.assetName;
  }

  // Template name format: "{jobName} {assets} at {propertyName}"
  // e.g., "Clean Pool Pump, Pool Filter at Beach House"
  const jobName = config.siteName
    ? `${config.jobName} ${assetNameDisplay} at ${config.siteName}`
    : `${config.jobName} ${assetNameDisplay}`;

  // If we have seeded template tasks, use them as a base and adapt for specific assets
  // Otherwise, create simple tasks from scratch
  // NOTE: Purchase and Stocktake task types are currently unused/disabled in the codebase.
  // The tasks array type matches TaskInput from GraphQL schema
  type TemplateTask = {
    id: string;
    type: string;
    name: string;
    description: string | null;
    attributes?: string[]; // Array of attribute IDs (matches TaskInput)
    jobAttributes?: string[];
    tagIds?: string[];
    asset?: {
      selection: string;
      assets: string[];
      allow: string[];
      spaces?: string[];
      assetTypes?: string[];
      shareAttributes?: boolean;
      shareJobHistory?: boolean;
      shareDeploymentHistory?: boolean;
    };
    attribute?: {
      attributes: string[];
    };
  };

  // Task mapper for TemplateTask format
  const templateTaskMapper: TaskMapper<TemplateTask> = (
    templateTask,
    assetContext,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _assetCount
  ) => {
    // Fallback case: no template task, create simple Asset task
    // Uses fallbackTaskDescription (e.g. frontend-defined checklist) when no backend template matched
    if (!templateTask && assetContext) {
      return [
        {
          id: nanoid(),
          type: 'Asset' as const,
          name: assetContext.taskTitle,
          description: config.fallbackTaskDescription ?? null,
          asset: {
            selection: 'specific' as const,
            assets: [assetContext.assetId],
            allow: ['edit'],
          },
        },
      ];
    }

    // Template task case
    if (!templateTask) return [];

    // Extract tag IDs from tags connection
    const tagIds = templateTask.tags?.nodes?.map((tag) => tag.id) ?? [];

    // Asset task case: use asset context to build task
    if (assetContext) {
      return [
        {
          id: nanoid(),
          type: templateTask.type,
          name: assetContext.taskTitle,
          description: templateTask.description, // Preserve description from template
          // Extract attribute IDs from attribute objects (TaskInput expects string[])
          attributes: templateTask.attributes?.map((attr) => attr.id),
          jobAttributes: templateTask.jobAttributeIds ?? undefined,
          tagIds,
          asset: {
            selection: 'specific' as const,
            assets: [assetContext.assetId],
            allow:
              templateTask.config?.allow && templateTask.config.allow.length > 0
                ? templateTask.config.allow
                : ['edit'],
            spaces: templateTask.config?.spaces,
            assetTypes: templateTask.config?.assetTypes,
            shareAttributes: templateTask.config?.shareAttributes ?? true,
            shareJobHistory: templateTask.config?.shareJobHistory ?? true,
            shareDeploymentHistory:
              templateTask.config?.shareDeploymentHistory ?? false,
          },
        },
      ];
    }

    // Non-Asset task case: copy as-is (e.g., Attribute, Purchase, Stocktake, etc.)
    const task: TemplateTask = {
      id: nanoid(),
      type: templateTask.type,
      name: templateTask.name,
      description: templateTask.description,
      // Extract attribute IDs from attribute objects (TaskInput expects string[])
      attributes: templateTask.attributes?.map((attr) => attr.id),
      jobAttributes: templateTask.jobAttributeIds ?? undefined,
      tagIds,
    };

    // Add type-specific fields
    if (templateTask.type === 'Attribute' && templateTask.attribute) {
      task.attribute = {
        attributes: templateTask.attribute.attributes,
      };
    }

    // NOTE: Purchase and Stocktake task types are currently unused/disabled in the codebase.
    // The handling code below is commented out but preserved for future use if these features are re-enabled.
    // if (templateTask.type === 'Purchase' && templateTask.purchase) {
    //   task.purchase = {
    //     spaces: templateTask.purchase.spaces,
    //   };
    // }
    // if (templateTask.type === 'Stocktake' && templateTask.stocktake) {
    //   task.stocktake = {
    //     include: templateTask.stocktake.include,
    //     itemStatus: templateTask.stocktake.itemStatus,
    //     spaces: templateTask.stocktake.spaces,
    //   };
    // }

    return [task];
  };

  const tasks = generateTasksFromSeededTemplate(
    seededTemplateTasks,
    uniqueAssetIds,
    {
      jobName: config.jobName,
      assetName: config.assetName,
      assetIdToNameMap: config.assetIdToNameMap,
      assetIdToTypeMap: config.assetIdToTypeMap,
      assetIdToSpaceMap: config.assetIdToSpaceMap,
    },
    assetCount,
    templateTaskMapper
  );

  // Get display-friendly frequency for notes
  const freqDisplay =
    config.freq === 'fortnightly' ? 'fortnightly' : config.freq;

  // Build notes with proper asset name display (same logic as template name)
  // Validate assetNames length matches unique assetIds length
  const notesAssetDisplay =
    assetCount > 1 &&
    config.assetNames &&
    config.assetNames.length > 1 &&
    config.assetNames.length === uniqueAssetIds.length
      ? formatAssetNamesForDisplay(config.assetNames) // Multiple different assets - group, pluralize, join with "and"
      : assetCount > 1
      ? pluralize(config.assetName) // Multiple units of same asset - pluralize
      : config.assetName; // Single asset

  return {
    name: jobName,
    status: Status.Active,
    notes: `Automated recurring ${freqDisplay} job for ${notesAssetDisplay}`,
    tasks,
    tags: [],
    attachments: [],
    includeAttributes: null,
    jobAttributes: null,
  };
}

/**
 * Builds an automation input for a recurring asset job.
 * The automation will create jobs on the specified schedule indefinitely.
 *
 * Handles frequency normalization:
 * - 'fortnightly' → 'weekly' with interval 2
 * - 'custom' → uses customUnit as actual frequency
 * - byday values converted from full names ('Monday') to 2-letter codes ('mo')
 *
 * @param siteId - The site where jobs will be created
 * @param templateId - The job template to use
 * @param tenantTimezone - Timezone for scheduling
 * @param userContactId - Owner of the jobs (can be null)
 * @param config - Automation configuration
 */
export function buildRecurringAssetAutomationInput(
  siteId: string,
  templateId: string,
  tenantTimezone: string,
  userContactId: string | null,
  config: RecurringAssetAutomationConfig
): AutomationInput {
  const assetCount = config.assetIds.length;

  // Validate that assetNames length matches assetIds length if assetNames is provided
  let assetNameDisplay: string;
  if (
    config.assetNames &&
    config.assetNames.length > 0 &&
    config.assetNames.length !== assetCount
  ) {
    // Mismatch detected - log warning and fall back to single asset name
    console.warn(
      `[buildRecurringAssetAutomationInput] Asset names array length (${config.assetNames.length}) does not match asset IDs length (${assetCount}). Falling back to single asset name.`
    );
    assetNameDisplay =
      assetCount > 1 ? pluralize(config.assetName) : config.assetName;
  } else if (
    assetCount > 1 &&
    config.assetNames &&
    config.assetNames.length > 1
  ) {
    // Multiple different assets - group by type, pluralize when needed, join with "and"
    assetNameDisplay = formatAssetNamesForDisplay(config.assetNames);
  } else if (assetCount > 1) {
    // Multiple units of same asset - pluralize
    assetNameDisplay = pluralize(config.assetName);
  } else {
    // Single asset
    assetNameDisplay = config.assetName;
  }
  const jobName = `${config.jobName} ${assetNameDisplay}`;

  // Normalize frequency (handles fortnightly, custom, byday conversion)
  // Re-use the same normalization logic as job creation for consistency
  const normalizedConfig: RecurringAssetJobConfig = {
    assetIds: config.assetIds,
    assetName: config.assetName,
    jobName: config.jobName,
    freq: config.freq,
    interval: config.interval,
    byday: config.byday,
    bymonthday: config.bymonthday,
    startDate: config.startDate,
    customUnit: config.customUnit,
  };
  const {
    freq: normalizedFreq,
    interval,
    byday,
  } = normalizeFrequency(normalizedConfig);

  // Build repeat schedule with normalized values
  const repeat = {
    freq: normalizedFreq,
    interval,
    ...(byday && byday.length > 0 ? { byday } : {}),
    ...(config.bymonthday ? { bymonthday: config.bymonthday } : {}),
  };

  // Format start date with time (midnight in tenant timezone)
  const repeatStartDate = `${config.startDate}T00:00:00`;

  // Default maintainRepeats to 10 if not specified, matching CreateJobForm behavior
  const maintainRepeats = config.maintainRepeats ?? 10;

  return {
    name: jobName,
    status: Status.Active,
    on: 'repeat',
    whereType: AutomationWhereType.Select,
    matchAttribute: [],
    sites: [siteId],
    jobName: jobName,
    template: templateId,
    assetIds: config.assetIds, // Pass asset IDs to automation for subsequent job generation
    owner: userContactId ? [userContactId] : null,
    assignee: null, // No assignee as per requirements
    day: 0, // Same day
    time: null, // No specific time
    repeatStartDate,
    repeat,
    runOnExistingBookings: false,
    maintainRepeats,
  };
}
